<script setup>
import { i18n } from 'boot/i18n';
import {
  HrCheckbox,
  HrFormSectionHeader,
  HrIconHintBtn,
  HrList,
  HrListAddBtn,
  HrListExpansion,
  HrListSectionHeader,
  HrListSelect,
  HrMultiSelect,
  HrOverflowMenuBtn,
  HrSegmentedBtn,
  HrTextField,
  HrVersionedFormDialog,
  HrWarningBanner,
  notifyNegative,
  useDialog,
} from 'src/design-system';
import { computed, onMounted, ref, watch } from 'vue';

import {
  BILLING_CHANNEL,
  BILLS_UNDER,
  carriesPriorAuthorization,
  DEFAULT_BILLING_CHANNEL_BY_ARCHETYPE,
  FUNDING_SOURCE_ARCHETYPE,
  namesRenderingProvider,
  TELEHEALTH_MODIFIERS,
} from '../../domain/constants/fundingSource';
import { MAX_MODIFIERS_PER_LINE } from '../../domain/constants/modifier';
import { latestStart } from '../../domain/services/fundingSourceDating';
import { useChargeMasterCodes } from '../composables/useChargeMaster';
import { useFundingSourceVersions } from '../composables/useFundingSourceVersions';
import { useServiceCodes } from '../composables/useServiceCodes';
import AddPayerRuleDialog from './AddPayerRuleDialog.vue';
import ModifierRuleEditor from './ModifierRuleEditor.vue';
import {
  payerCodeOptions,
  placeCaption,
  serviceCaption,
  serviceLabel,
  withModifiers,
  withPlaceOverride,
  withServiceCode,
} from '../presenters/codingRuleRows';
import {
  isDraftDirty,
  servicesExceedingLine,
  servicesWithRuleProblems,
  toDraft,
} from '../presenters/fundingSourceDraft';
import { modifierSuggestionOptions } from '../presenters/modifierOptions';
import {
  effectiveDateFor,
  presentMaxDate,
  presentMinDate,
  presentReplacementWarning,
  presentSaveChoices,
  presentVersionLine,
} from '../presenters/fundingSourceVersionPresenter';

/**
 * Add or edit one funding source — a payer and the policy for billing them.
 *
 * Replaces the legacy `components/Billing/EditFundingSourceDialog`, which was a Quasar dialog
 * fixed at 500px. It lives here rather than in `components/Billing` because a payer is this
 * context's own concept; the settings page is a host, the way the client editor hosts the coverage
 * panel.
 *
 * FIELD ORDER IS THE SPECIALIST'S REASONING ORDER — who, what kind, how billed, then the
 * catch-all. Type before channel is not cosmetic: it is what lets the archetype pre-fill the
 * channel, so the common path is "pick the type and move on" and only the edge cases touch the
 * channel at all.
 *
 * NO DESTRUCTIVE ACTION HERE. Inactivating a funder is taken from the row's ⋮ menu on the settings
 * page, matching the coverage dialog's split for the same reason: a form holds a record's own
 * facts, and a menu action reads as something done TO the record. It also keeps the usage guard
 * (`checkFundingSourceUsage`) on the surface that knows about the row.
 *
 * IT EDITS A MAPPED FundingSource, not the stored document, which is a reversal of how this dialog
 * started. It used to say `fundingSourceName` because the settings page read and wrote documents
 * directly and there was no write-side mapper. There is one now, so the form speaks the model's
 * vocabulary and the mapper does the translating — which is what keeps the document's field names
 * from spreading back into a component.
 *
 * COVERED SERVICES ARE ENTRIES, AND THE PICKER SPEAKS IDS — a distinction that is load-bearing
 * rather than incidental. The legacy page resolved each entry against the treatment-services list for
 * display, handed the resolved row straight to its dialog, and the dialog emitted it back, so every
 * save wrote a full treatment-service snapshot into the funder's config. Nothing here resolves
 * anything, and the multi-select binds to ids through a computed that RECONCILES against the existing
 * entries rather than rebuilding them — so an entry keeps its per-service overrides when a different
 * service is added or removed. Those overrides are the next piece of work, and that is when the
 * picker becomes a list of expandable rows rather than a multi-select.
 */
defineOptions({ name: 'EditFundingSourceDialog' });

const props = defineProps({
  /** The payer being edited — its `fundingSourceId`, never a version id. Null when adding. */
  fundingSourceId: {
    type: String,
    default: null,
  },
  /** The clinic whose payers these are. */
  clinicId: {
    type: String,
    required: true,
  },
  /** The clinic's treatment services, for the covered-services picker. */
  treatmentServices: {
    type: Array,
    default: () => [],
  },
  /** The clinic's places of service, for the place-of-service override rows. */
  placesOfService: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits({
  /** The saved payer's `fundingSourceId` — the series, so the host can refresh and re-select it. */
  ok: null,
  hide: null,
});

const dialog = ref(null);
const { confirmDestructiveDialog } = useDialog();

/**
 * The timeline, and the only thing here that touches persistence. It reads the clock once, so every
 * date decision below — the floor, which options are offered, what counts as in force — stays fixed
 * for as long as the dialog is open.
 */
const versions = useFundingSourceVersions();

/** The chosen save option and, for the option that offers a calendar, its date. */
const saveChoice = ref(null);
const pickedDate = ref(null);

/**
 * The write in flight — deliberately NOT the composable's `isBusy`, which is also true while the
 * series loads. Reusing it would spin the confirm button every time the dialog opens, which reads as
 * "saving" at the one moment nothing is being saved.
 */
const isSaving = ref(false);

const isAdding = computed(() => props.fundingSourceId === null);

/*
 * The form's working copy lives in `presenters/fundingSourceDraft`, not here, because the dirty
 * check below is only as good as the agreement between `toDraft` and the mapper — and that
 * agreement needs a test.
 */

const draft = ref(toDraft(null));

const serviceCodes = useServiceCodes();
const chargeMasterCodes = useChargeMasterCodes();
const payerCodesLoadFailed = ref(false);

/**
 * The codes a payer may bill a service under: valid today and on the clinic's charge master, the
 * narrowing the treatment service editor applies to a service's own code, so a payer code always has
 * a price when it is picked.
 *
 * NOTHING IS OFFERED BEFORE THE CHARGE MASTER HAS ANSWERED. "Not on the charge master" and "not read
 * yet" are different answers, and an empty list would read as a clinic that bills on nothing.
 */
const offeredPayerCodes = computed(() => {
  if (!chargeMasterCodes.hasLoaded.value) {
    return [];
  }

  return serviceCodes.selectableCodes.value.filter((code) => chargeMasterCodes.isOnChargeMaster(code.id));
});

onMounted(() => {
  Promise.all([
    serviceCodes.load({ clinicId: props.clinicId }),
    chargeMasterCodes.load({ clinicId: props.clinicId }),
  ]).catch((error) => {
    payerCodesLoadFailed.value = true;
    console.error('Failed to load the codes a payer can bill under', error);
  });
});

/** Reported on the row as well as gating Save; the rule itself lives with `toDraft`, where it is tested. */
const modifierSuggestions = computed(() => modifierSuggestionOptions());

const overlongServiceIds = computed(() => servicesExceedingLine(draft.value));

/** A failed read is said on the control, because its consequence is the empty list right there. */
function payerCodeHint(ownCode) {
  if (payerCodesLoadFailed.value) {
    return i18n.t('settings.fundingSources.coding.inputPayerCodeLoadFailed');
  }

  return ownCode === null
    ? i18n.t('settings.fundingSources.coding.inputPayerCodeHintNoOwnCode')
    : i18n.t('settings.fundingSources.coding.inputPayerCodeHint', { code: ownCode });
}

/**
 * Services whose conditional modifier rules cannot be saved as they stand.
 *
 * REFUSED AT THE POINT OF AUTHORING, like an overlong modifier set, because both faults are silent
 * afterwards: a rule with no modifier holds every performer it matches while looking configured, and
 * a rule nothing can reach is a tier somebody believes they wrote. Neither surfaces until a claim
 * does not go out.
 */
const ruleProblemServiceIds = computed(() => servicesWithRuleProblems(draft.value));

/**
 * The services this payer covers, as rows: the entry the payer holds for each, plus the label the
 * picker offered it under.
 *
 * DRIVEN BY THE DRAFT, not by the catalog, so the order is the order they were added and a service
 * deleted from the clinic's catalog still shows the rule the payer carries for it — as its id rather
 * than as a blank row, which is the honest way to say "this rule references something gone".
 */
const coveredServiceRows = computed(() => {
  return draft.value.coveredServices.map((entry) => {
    const treatmentService = props.treatmentServices
      .find((candidate) => candidate.id === entry.treatmentServiceId);

    const isOverlong = overlongServiceIds.value.has(entry.treatmentServiceId);
    const payerCodeId = entry.serviceCodeId ?? null;
    const payerCode = serviceCodes.codeById(payerCodeId);
    const ownCode = treatmentService?.serviceCode || null;

    return {
      entry,
      label: treatmentService ? serviceLabel(treatmentService) : entry.treatmentServiceId,
      // The id stands in once the catalog has answered without it, the way a deleted service shows
      // its id above: a blank would claim the payer names no code.
      caption: serviceCaption(
        entry,
        isOverlong,
        ruleProblemServiceIds.value.has(entry.treatmentServiceId),
        payerCode?.code ?? (payerCodeId !== null && serviceCodes.hasLoaded.value ? payerCodeId : null),
      ),
      isOverlong,
      payerCodeOptions: payerCodeOptions(offeredPayerCodes.value, {
        selectedId: payerCodeId,
        ownCodeId: treatmentService?.defaultServiceCodeId ?? null,
        selectedCode: payerCode,
      }),
      payerCodeHint: payerCodeHint(ownCode),
    };
  });
});

/**
 * Places added in this sitting, so their row can open on arrival.
 *
 * A place override is the one add on this form that arrives UNFINISHED — an override with no code
 * means nothing and is dropped on save — so landing a collapsed row would hide the only part that
 * matters. Covered services are deliberately not treated this way: a service with no modifiers is a
 * finished row, and opening fifteen of them at once after a multi-add would bury the list.
 *
 * It tracks ids rather than driving each row's open state, so `defaultOpened` only decides how a row
 * ARRIVES. Collapsing it afterwards sticks, which a bound `modelValue` would fight.
 */
const newPlaceOverrideIds = ref(new Set());

/**
 * Places this payer renders differently, as rows. Only places carrying an override appear.
 *
 * Each row names the place's OWN code twice over, in the caption and in a hint under the field,
 * because "renders as POS 02" cannot be judged without knowing what it replaced — and the judgement a
 * reader is here to make is whether the override still earns its keep.
 *
 * A HINT RATHER THAN A PLACEHOLDER, which is where this first went wrong. A placeholder on an
 * outlined field is hidden until the label floats, so it shows the fallback code only to somebody
 * already typing over it and shows nothing at all to somebody reading the row — the exact inverse of
 * when the fact is wanted. The hint states the consequence rather than the bare code, because "Uses
 * 12" leaves the reader to infer what "uses" means and when.
 *
 * A place with no code of its own says so, rather than borrowing a fabricated one: there the override
 * is not replacing anything, it is supplying the only code the place has.
 */
const placeOverrideRows = computed(() => {
  return draft.value.placeOfServiceOverrides.map((override) => {
    const place = props.placesOfService
      .find((candidate) => candidate.id === override.placeOfServiceId);
    const ownCode = place?.placeOfServiceCode ?? null;
    const codingKeys = 'settings.fundingSources.coding';

    return {
      override,
      label: place?.placeOfServiceName ?? override.placeOfServiceId,
      ownCode,
      caption: placeCaption(override, ownCode),
      hint: ownCode
        ? i18n.t(`${codingKeys}.inputPlaceCodeHint`, { code: ownCode })
        : i18n.t(`${codingKeys}.inputPlaceCodeHintNoOwnCode`),
      isNew: newPlaceOverrideIds.value.has(override.placeOfServiceId),
    };
  });
});

/**
 * What the add pickers may still offer.
 *
 * A service already covered is REMOVED rather than marked, unlike the approved-service picker one
 * aggregate over: there a funder can legitimately approve one service twice under different
 * settings, so the picker says what is used. Here a second entry for the same service is not
 * expressible — the entry IS the payer's configuration for it — so offering one would be offering a
 * choice that cannot be made.
 */
const addableServiceOptions = computed(() => {
  const covered = new Set(draft.value.coveredServices.map((entry) => entry.treatmentServiceId));

  return props.treatmentServices
    // A non-billable service produces no claim — the projector holds it NOT_BILLABLE before any
    // payer rule is read — so a coding rule authored for one could never apply. Offered, it would be
    // a row somebody fills in and never sees take effect.
    //
    // `=== false`, MIRRORING THE RESOLVER exactly rather than asking for a truthy value. Billability
    // is explicit and a service written before the field existed carries nothing; treating absence as
    // non-billable would hide services the projector bills perfectly well, and the picker and the
    // engine would disagree about what is billable.
    .filter((treatmentService) => treatmentService.isBillable !== false)
    .filter((treatmentService) => !covered.has(treatmentService.id))
    .map((treatmentService) => ({ label: serviceLabel(treatmentService), value: treatmentService.id }))
    .sort((a, b) => a.label.localeCompare(b.label));
});

const addablePlaceOptions = computed(() => {
  const overridden = new Set(
    draft.value.placeOfServiceOverrides.map((override) => override.placeOfServiceId),
  );

  return props.placesOfService
    .filter((place) => !overridden.has(place.id))
    .map((place) => ({ label: place.placeOfServiceName ?? place.id, value: place.id }))
    .sort((a, b) => a.label.localeCompare(b.label));
});

// Each list ends in an add ROW that opens its picker, the pattern the authorization editor uses.
const servicePicker = ref(null);
const placePicker = ref(null);

function addCoveredServices(treatmentServiceIds) {
  draft.value.coveredServices = draft.value.coveredServices
    .concat(treatmentServiceIds.map((treatmentServiceId) => ({ treatmentServiceId, modifiers: [] })));
}

/**
 * Removing a service takes its payer rules with it, and the rules are the part that is expensive to
 * re-enter — so this asks, where dropping a place override (one code, re-picked in two clicks) does
 * not. Both are undoable by not saving, but only one of them costs anything to redo.
 */
function removeCoveredService(treatmentServiceId, label) {
  confirmDestructiveDialog(
    i18n.t('settings.fundingSources.coding.removeServiceTitle'),
    i18n.t('settings.fundingSources.coding.removeServiceMessage', { service: label }),
    () => {
      draft.value.coveredServices = draft.value.coveredServices
        .filter((entry) => entry.treatmentServiceId !== treatmentServiceId);
    },
    { confirmLabel: i18n.t('common.buttonDelete') },
  );
}

function setModifiers(treatmentServiceId, modifiers) {
  draft.value.coveredServices = withModifiers(draft.value.coveredServices, treatmentServiceId, modifiers);
}

function setPayerCode(treatmentServiceId, serviceCodeId) {
  draft.value.coveredServices = withServiceCode(draft.value.coveredServices, treatmentServiceId, serviceCodeId);
}

/**
 * The payer's conditional rows for one service.
 *
 * A WHOLE-LIST REPLACEMENT rather than a per-row edit, because order is meaning here: the editor
 * reorders, inserts and removes, and each of those changes what the list says. Taking back the list
 * it produced keeps one definition of the order rather than two that can disagree.
 */
function setModifierRules(treatmentServiceId, modifierRules) {
  draft.value.coveredServices = draft.value.coveredServices.map((entry) => {
    return entry.treatmentServiceId === treatmentServiceId ? { ...entry, modifierRules } : entry;
  });
}

/**
 * Rows open EMPTY, not seeded with the place's own code.
 *
 * Seeding looked helpful and was the wrong way round: a row nobody edits then stores an override
 * that renders the place exactly as the clinic already records it, which is noise that resolves to
 * nothing. Empty is self-cleaning — the mapper drops an override carrying no code, so adding a place
 * and changing your mind leaves nothing behind. The field's placeholder says which code stands in
 * the meantime.
 *
 * This is what makes taking several at once safe: pick four, fill one, and the other three go away.
 */
function addPlaceOverrides(placeOfServiceIds) {
  draft.value.placeOfServiceOverrides = placeOfServiceIds.reduce((overrides, placeOfServiceId) => {
    return withPlaceOverride(overrides, placeOfServiceId, '');
  }, draft.value.placeOfServiceOverrides);

  placeOfServiceIds.forEach((placeOfServiceId) => newPlaceOverrideIds.value.add(placeOfServiceId));
}

function removePlaceOverride(placeOfServiceId) {
  draft.value.placeOfServiceOverrides = draft.value.placeOfServiceOverrides
    .filter((override) => override.placeOfServiceId !== placeOfServiceId);
}

function setPlaceCode(placeOfServiceId, code) {
  const overrides = draft.value.placeOfServiceOverrides;

  draft.value.placeOfServiceOverrides = withPlaceOverride(overrides, placeOfServiceId, code);
}

/**
 * The draft as it was seeded, kept to answer "has anything actually changed".
 *
 * A separate `toDraft` call rather than a reference to the same object, so editing the draft does not
 * quietly edit the thing it is being compared against.
 */
const baseline = ref(toDraft(null));

const seedFrom = (record) => {
  draft.value = toDraft(record);
  baseline.value = toDraft(record);
  newPlaceOverrideIds.value = new Set();
};

/** Guards against ghost versions; the comparison itself lives with `toDraft`, where it is tested. */
const isDirty = computed(() => isDraftDirty(draft.value, baseline.value));

/*
 * DOES NOT SEED — the watcher below does, and it is the only thing that should. `selectedRecord` is
 * a computed over `versions`, so the assignment inside `load` moves it from null to a record and the
 * watcher fires on that; a `seedFrom` here as well ran it twice with the same record. Harmless,
 * because seeding is idempotent, but two seeding paths is what made a draft/baseline disagreement
 * hard to see the last time one existed. Adding the call back would restore that.
 */
onMounted(async () => {
  if (isAdding.value) {
    return;
  }
  await versions.load({ fundingSourceId: props.fundingSourceId, clinicId: props.clinicId });
});

/**
 * Reseeds the form when the user switches to another version.
 *
 * Watched on the selected record rather than done in the switch handler, because the composable
 * decides which version is selected — including the initial choice of the scheduled change over the
 * one in force — and the form has to follow that rather than duplicate the rule.
 */
watch(() => versions.selectedRecord.value, (record) => {
  if (record !== null) {
    // Reseeds the baseline too: switching to a version and saving it untouched would be a ghost
    // version of that version, which is the same mistake in a less obvious place.
    seedFrom(record);
  }
});

const versionLine = computed(() => presentVersionLine(versions.timeline.value, {
  asOf: versions.asOf,
  selected: versions.selected.value,
}));

const saveChoices = computed(() => presentSaveChoices(versions.timeline.value, {
  asOf: versions.asOf,
  selected: versions.selected.value,
}));

/** The date the current choice resolves to, or null while the choice is incomplete. */
const effectiveFrom = computed(() => effectiveDateFor(saveChoice.value, {
  asOf: versions.asOf,
  selected: versions.selected.value,
  pickedDate: pickedDate.value,
}));

const replacementWarning = computed(() => presentReplacementWarning(versions.timeline.value, {
  asOf: versions.asOf,
  effectiveFrom: effectiveFrom.value,
}));

const title = computed(() => {
  return isAdding.value
    ? i18n.t('settings.fundingSources.editFundingSource.addTitle')
    : i18n.t('settings.fundingSources.editFundingSource.editTitle');
});

const archetypeOptions = computed(() => {
  return Object.values(FUNDING_SOURCE_ARCHETYPE).map((archetype) => ({
    label: i18n.t(`fundingSourceArchetypes.${archetype}.label`),
    caption: i18n.t(`fundingSourceArchetypes.${archetype}.caption`),
    value: archetype,
  }));
});

const billingChannelOptions = computed(() => {
  return Object.values(BILLING_CHANNEL).map((billingChannel) => ({
    label: i18n.t(`billingChannels.${billingChannel}.label`),
    caption: i18n.t(`billingChannels.${billingChannel}.caption`),
    value: billingChannel,
  }));
});

const billsUnderOptions = computed(() => {
  // No caption: a segment shows a label and nothing else, and carrying one the control cannot render
  // would leave a dictionary entry looking used when it is not.
  return Object.values(BILLS_UNDER).map((billsUnder) => ({
    label: i18n.t(`billsUnder.${billsUnder}`),
    value: billsUnder,
  }));
});

/**
 * "Who bills" is only a question when the claim has somewhere to put the answer.
 *
 * Gated on the CHANNEL rather than the archetype: a school district billing Medicaid files claims and
 * names a renderer, and `archetype === 'commercial_insurer'` would have missed it. Asking an invoiced
 * funder who renders is asking about a field its bills do not have.
 */
const asksWhoBills = computed(() => namesRenderingProvider(draft.value.billingChannel));

/**
 * Is authorization even a question for this channel?
 *
 * Prior authorization is an adjudication concept — a payer agrees in advance and the claim references
 * that agreement. An invoice is billed as delivered and a roster is a headcount, so neither has an
 * authorization to require. Asking anyway invites a meaningless answer that then rides on the record.
 */
const asksForAuthorization = computed(() => carriesPriorAuthorization(draft.value.billingChannel));

/**
 * Keep both channel-dependent fields in step with what the channel can express.
 *
 * Clearing on the way out matters for each: hiding a field while keeping its value would let a funder
 * switched to invoice carry a rule or a requirement that silently reappears if it is ever switched
 * back — a decision nobody re-made, resurfacing as though somebody had. The mapper clears both on
 * write for the same reason; doing it here too keeps the form honest about what it will save.
 *
 * ON THE WAY BACK IN THE TWO DIVERGE, and the difference is the whole defaulting rule. Who bills is
 * left UNSET, because no answer is safe and picking one is how a claim names a renderer nobody chose.
 * Billing without authorization is set to OFF — that is, authorization required — because there the
 * two directions fail differently: a needless hold is visible and clearable, a claim for unauthorized
 * work is neither. Naming the field for the exception is what makes the safe value the falsy one.
 */
watch(() => draft.value.billingChannel, () => {
  if (!asksWhoBills.value) {
    draft.value.billsUnder = null;
  }

  if (!asksForAuthorization.value) {
    draft.value.billWithoutAuthorization = null;
  } else if (draft.value.billWithoutAuthorization === null) {
    draft.value.billWithoutAuthorization = false;
  }
});

/**
 * Who bills is required, but only where the channel can express it.
 *
 * The one field in this form that cannot report its own validity: HrSegmentedBtn is a primitive rather
 * than a Quasar field, so it does not join HrForm's `validate()` the way the selects above do. The save
 * is therefore gated here and the requirement stated under the control, which also means the block
 * happens on the FORM step — before the user picks an effective date and only then finds out.
 */
const isWhoBillsAnswered = computed(() => !asksWhoBills.value || draft.value.billsUnder !== null);

/**
 * At least one covered service, stated here rather than as a field rule.
 *
 * The list is not a Quasar field, so it cannot join `HrForm`'s `validate()` the way the selects do —
 * the same reason `isWhoBillsAnswered` is gated here. Being on the FORM step matters: the user finds
 * out before picking an effective date rather than after.
 */
const isAnyServiceCovered = computed(() => draft.value.coveredServices.length > 0);

/**
 * Just the modifiers. No "none" option, because the EMPTY FIELD is that answer.
 *
 * An option reading "No modifier" made the control disagree with its own hint, which says to leave
 * it empty — and it asked somebody to open a select to record that they are not using one. Clearing
 * is the same answer with no ceremony. There is deliberately no "unconfigured" state either: it
 * would produce the same claim as "wants none", so nothing reads the difference.
 */
const telehealthModifierOptions = computed(() => {
  return TELEHEALTH_MODIFIERS.map((modifier) => ({ label: modifier, value: modifier }));
});

/**
 * Pre-fill the channel from the archetype, WITHOUT clobbering a deliberate override.
 *
 * The channel is only rewritten when it is empty or still holds the previous archetype's default —
 * i.e. when nobody has expressed an opinion about it. A specialist who set a school district to
 * electronic claims because it bills Medicaid keeps that when they correct the archetype; the
 * district-billing-Medicaid case is exactly why these are two fields.
 */
watch(() => draft.value.archetype, (archetype, previousArchetype) => {
  const wasUntouched = draft.value.billingChannel === null
    || draft.value.billingChannel === DEFAULT_BILLING_CHANNEL_BY_ARCHETYPE[previousArchetype];

  if (wasUntouched) {
    draft.value.billingChannel = DEFAULT_BILLING_CHANNEL_BY_ARCHETYPE[archetype] ?? null;
  }
});

const nameRules = [
  (value) => !!value?.trim()
    || i18n.t('settings.fundingSources.editFundingSource.inputNameError'),
];

// Required even though the migration stored nulls — that is the point. A funder that arrived
// from the legacy `other` or `regionalCenter` has an honestly unset archetype, and the first
// person to edit it is the one who can say what it actually is.
const archetypeRules = [
  (value) => !!value
    || i18n.t('settings.fundingSources.editFundingSource.inputArchetypeError'),
];

const billingChannelRules = [
  (value) => !!value
    || i18n.t('settings.fundingSources.editFundingSource.inputBillingChannelError'),
];

function hide() {
  dialog.value?.hide();
}

/**
 * Writes the save, then hands the PAYER's id back so the host can refresh its table.
 *
 * Two different writes behind one button: a payer being created starts a series, and one being edited
 * gets a version on the date the user chose. The dialog does the write itself rather than emitting the
 * record for the host to save, because only this layer knows which of the two it is and what date was
 * chosen — and the host is a settings page that should not learn either.
 *
 * A FAILED SAVE KEEPS THE DIALOG OPEN. `EffectiveDateRejected` means a colleague moved the floor while
 * this dialog was open, and `VersionCollision` means they wrote to the same date; in both cases the
 * user's edits are still the thing they want and closing would discard them. The message says what
 * happened and the timeline is re-read, so the options on the second attempt reflect what is now
 * stored.
 */
async function onSubmit() {
  // The series arrives asynchronously, so until it does an EDIT looks exactly like a create: no
  // version selected, no options offered. Submitting in that window would write a version dated today
  // against a timeline nobody has read. The load takes milliseconds, so this only ever catches a
  // genuinely broken read.
  if (!isAdding.value && versions.selectedRecord.value === null) {
    return;
  }

  // No date, no write. The confirm button is disabled until a complete choice is made, so reaching
  // here without one means the two disagree — and the write path would rightly refuse it. Failing
  // quietly beats a thrown error the user cannot act on.
  if (effectiveFrom.value === null) {
    return;
  }

  isSaving.value = true;
  try {
    if (isAdding.value) {
      emit('ok', await versions.create({
        clinicId: props.clinicId,
        effectiveFrom: effectiveFrom.value,
        record: draft.value,
      }));
    } else {
      await versions.commit({
        fundingSourceId: props.fundingSourceId,
        clinicId: props.clinicId,
        effectiveFrom: effectiveFrom.value,
        record: draft.value,
      });
      emit('ok', props.fundingSourceId);
    }
    hide();
  } catch (error) {
    notifyNegative(error.name === 'EffectiveDateRejected' || error.name === 'VersionCollision'
      ? i18n.t('settings.fundingSources.versions.saveConflict')
      : i18n.t('common.errors.generic'));

    if (!isAdding.value) {
      await versions.load({ fundingSourceId: props.fundingSourceId, clinicId: props.clinicId });
    }
  } finally {
    // Cleared in `finally` rather than after the reload: on the failure path the dialog stays open,
    // and a spinner left running would leave the user unable to try again.
    isSaving.value = false;
  }
}

defineExpose({
  show: () => dialog.value?.show(),
  hide,
});
</script>

<template>
  <hr-versioned-form-dialog
    ref="dialog"
    v-model:option="saveChoice"
    v-model:date="pickedDate"
    size="md"
    :title="title"
    :when-title="i18n.t('settings.fundingSources.versions.whenTitle')"
    :effective-from-label="versionLine.effectiveFromLabel"
    :version-label="versionLine.versionLabel"
    :version-options="versionLine.versionOptions"
    :save-options="saveChoices"
    :min-date="presentMinDate(versions.timeline.value, versions.asOf)"
    :max-date="presentMaxDate({
      asOf: versions.asOf,
      selected: versions.selected.value,
      latestFirstStart: latestStart(versions.asOf),
    })"
    :save-disabled="!isDirty || !isWhoBillsAnswered || !isAnyServiceCovered
      || overlongServiceIds.size > 0 || ruleProblemServiceIds.size > 0"
    :loading="isSaving"
    data-e2e="dialog-edit-funding-source"
    @select-version="versions.select($event)"
    @submit="onSubmit"
    @close="emit('hide')"
  >
    <template
      v-if="replacementWarning !== null"
      #warning
    >
      <hr-warning-banner :message="replacementWarning" />
    </template>

    <div class="row q-col-gutter-md">
      <!-- THREE GROUPS, AND THE ORDER IS WHOSE ANSWER EACH IS. Identity, then how WE submit to this
           payer, then what the PAYER demands. The first two are short and set once; the third is
           where a coordinator sits with the payer manual open, so it gets a heading that says where
           the manual maps onto the form. Flat, these nine fields changed subject four times without
           saying so. -->
      <hr-form-section-header
        class="col-12"
        :label="i18n.t('settings.fundingSources.groups.funder')"
      />

      <hr-text-field
        v-model="draft.name"
        class="col-12"
        :label="i18n.t('settings.fundingSources.fields.name')"
        :rules="nameRules"
        hide-bottom-space
        data-e2e="input-funding-source-name"
      />

      <hr-list-select
        v-model="draft.archetype"
        class="col-12"
        :label="i18n.t('settings.fundingSources.fields.archetype')"
        :options="archetypeOptions"
        :rules="archetypeRules"
        data-e2e="select-funding-source-archetype"
      />

      <hr-form-section-header
        class="col-12"
        :label="i18n.t('settings.fundingSources.groups.claimSubmission')"
      />

      <hr-list-select
        v-model="draft.billingChannel"
        class="col-12"
        :label="i18n.t('settings.fundingSources.fields.billingChannel')"
        :options="billingChannelOptions"
        :rules="billingChannelRules"
        data-e2e="select-funding-source-billing-channel"
      />

      <div
        v-if="asksWhoBills"
        class="col-12"
      >
        <div class="hr-body-small text-on-surface-variant q-mb-xs">
          {{ i18n.t('settings.fundingSources.fields.billsUnder') }}
        </div>
        <hr-segmented-btn
          v-model="draft.billsUnder"
          :options="billsUnderOptions"
          :aria-label="i18n.t('settings.fundingSources.fields.billsUnder')"
          spread
          data-e2e="segmented-funding-source-bills-under"
        />
        <!--
          Stated rather than coloured red. The control appears the moment the channel asks, so an
          unanswered one is a question not yet reached rather than a mistake — and it is what explains
          the disabled save, which would otherwise look broken.
        -->
        <div
          v-if="!isWhoBillsAnswered"
          class="hr-body-small text-on-surface-variant q-mt-xs"
        >
          {{ i18n.t('settings.fundingSources.editFundingSource.inputBillsUnderError') }}
        </div>
      </div>

      <hr-form-section-header
        class="col-12"
        :label="i18n.t('settings.fundingSources.groups.payerRules')"
      />

      <!-- The one field in the upper half the PAYER decides rather than us, so it belongs with the
           rules. The label still names OUR exception rather than their requirement: the stored flag
           is `billWithoutAuthorization`, named for its exception so the safe value is the falsy one,
           and a label reading the other way round would mean the opposite of the value behind it. -->
      <!-- A CHECKBOX RATHER THAN A SWITCH, and here more clearly than in any other form: saving
           does not just defer the change, it asks for a DATE. A switch reading "on" would claim a
           payer is billed without authorization today when the answer may not apply until next
           month. See HrSettingSwitch's @avoidWhen. -->
      <hr-checkbox
        v-if="asksForAuthorization"
        v-model="draft.billWithoutAuthorization"
        class="col-12"
        :label="i18n.t('settings.fundingSources.fields.billWithoutAuthorization')"
        data-e2e="checkbox-funding-source-bill-without-authorization"
      />

      <!-- THE ENTRY IS THE PAYER'S CONFIGURATION FOR THE SERVICE, so which services are covered and
           how each codes are one list rather than a picker beside a table. A row with no rules says
           nothing: an unconfigured payer is the ordinary state, and a line of "no rules" on every
           row makes the two that matter harder to find. -->
      <div class="col-12">
        <hr-list>
          <hr-list-section-header>
            {{ i18n.t('settings.fundingSources.coding.titleServices') }}
          </hr-list-section-header>
          <hr-list-expansion
            v-for="row in coveredServiceRows"
            :key="row.entry.treatmentServiceId"
            segmented
            flush
            :label="row.label"
            :caption="row.caption"
            data-e2e="expansion-funding-source-covered-service"
          >
            <template #trailing>
              <hr-overflow-menu-btn
                :options="[{
                  group: 'destructive',
                  options: [{
                    label: i18n.t('settings.fundingSources.coding.actionRemoveService'),
                    icon: 'delete',
                    destructive: true,
                    action: () => removeCoveredService(row.entry.treatmentServiceId, row.label),
                  }],
                }]"
                data-e2e="btn-funding-source-service-menu"
              />
            </template>
            <!-- q-expansion-item pads nothing, so the revealed content would sit flush against the
                 segmented card's fill. -->
            <div class="q-pa-md">
              <!-- ORDERED AS THE CLAIM LINE IS: the code, then what the performer's tier earned, then
                   the payer's unconditional set. The telehealth modifier is payer-level and reads last
                   of all. -->
              <div class="row q-col-gutter-md">
                <div class="col-12">
                  <hr-list-select
                    :model-value="row.entry.serviceCodeId ?? null"
                    :label="i18n.t('settings.fundingSources.coding.inputPayerCodeLabel')"
                    :hint="row.payerCodeHint"
                    :options="row.payerCodeOptions"
                    :loading="serviceCodes.isLoading.value || chargeMasterCodes.isLoading.value"
                    clearable
                    data-e2e="select-funding-source-service-payer-code"
                    @update:model-value="setPayerCode(row.entry.treatmentServiceId, $event)"
                  />
                </div>

                <div class="col-12">
                  <modifier-rule-editor
                    :model-value="row.entry.modifierRules ?? []"
                    @update:model-value="setModifierRules(row.entry.treatmentServiceId, $event)"
                  />
                </div>

                <div class="col-12">
                  <!-- SUGGESTIONS, NOT A CLOSED SET: anything typed is accepted, because each payer
                   decides which modifiers it wants and state Medicaid programs define their own. The
                   list is there to save retyping and to catch U03 typed for U3 — see the constant for
                   which families are deliberately absent. -->
                  <hr-multi-select
                    :model-value="row.entry.modifiers ?? []"
                    :label="i18n.t('settings.fundingSources.coding.inputModifiersLabel')"
                    :hint="i18n.t('settings.fundingSources.coding.inputModifiersHint')"
                    :options="modifierSuggestions"
                    :max-values="MAX_MODIFIERS_PER_LINE"
                    :error="row.isOverlong"
                    :error-message="i18n.t('settings.fundingSources.coding.errorModifiersExceedLine')"
                    use-input
                    new-value-mode="add-unique"
                    data-e2e="select-funding-source-service-modifiers"
                    @update:model-value="setModifiers(row.entry.treatmentServiceId, $event)"
                  />
                </div>
              </div>
            </div>
          </hr-list-expansion>
          <!-- Both lists sit in one group, so their add rows render alike: an outlined row beside a
               flat one reads as an arbitrary difference rather than as "this one is empty". The
               empty state is carried by the supporting text below instead. -->
          <hr-list-add-btn
            variant="flat"
            :label="i18n.t('settings.fundingSources.coding.actionAddServices')"
            :disable="addableServiceOptions.length === 0"
            flush
            data-e2e="btn-funding-source-add-service"
            @click="servicePicker?.show()"
          />
        </hr-list>
        <!-- INSTRUCTION, NOT AN ERROR. An empty list is where a new payer starts, so reporting it
             as a failure accuses somebody of something they have not had a chance to do. It still
             has to be said: it is the only thing on screen explaining the disabled save. -->
        <!-- `q-px-md` to sit under the list's own 16px inset: the section header above it takes that
             from HrItemLabel, so text at the container edge reads as belonging to the form rather
             than to the list it is about. -->
        <div
          v-if="!isAnyServiceCovered"
          class="hr-body-small text-on-surface-variant q-mt-xs q-px-md"
        >
          {{ i18n.t('settings.fundingSources.coding.hintNoServices') }}
        </div>
      </div>

      <!-- AN EXCEPTION LIST, NOT A COVERAGE LIST, and it sits directly under one that IS — so it is
           named for the override rather than for the place. Which settings a payer approves is an
           AUTHORIZATION fact, carried per limit and keyed on the POS CODE, because a clinic can hold
           several records sharing one code and the letter scopes the setting rather than our record
           of it. This list is keyed on our record, because it is about what code that record renders
           under. Different keys, different subjects. -->
      <div class="col-12">
        <hr-list>
          <hr-list-section-header>
            {{ i18n.t('settings.fundingSources.coding.titlePlaces') }}
            <template #trailing>
              <!-- The explanation rides the header rather than sitting under the list: an empty
                   override list is the ordinary state, so the words are there for somebody wondering
                   WHY it is empty, not for everybody every time. -->
              <hr-icon-hint-btn
                :hint="i18n.t('settings.fundingSources.coding.hintNoPlaces')"
                data-e2e="btn-funding-source-places-hint"
              />
            </template>
          </hr-list-section-header>
          <hr-list-expansion
            v-for="row in placeOverrideRows"
            :key="row.override.placeOfServiceId"
            segmented
            flush
            :label="row.label"
            :caption="row.caption"
            :default-opened="row.isNew"
            data-e2e="expansion-funding-source-place-override"
          >
            <template #trailing>
              <hr-overflow-menu-btn
                :options="[{
                  group: 'destructive',
                  options: [{
                    label: i18n.t('settings.fundingSources.coding.actionRemovePlace'),
                    icon: 'delete',
                    destructive: true,
                    action: () => removePlaceOverride(row.override.placeOfServiceId),
                  }],
                }]"
                data-e2e="btn-funding-source-place-menu"
              />
            </template>
            <div class="q-pa-md">
              <hr-text-field
                :model-value="row.override.placeOfServiceCode"
                :label="i18n.t('settings.fundingSources.coding.inputPlaceCodeLabel')"
                :hint="row.hint"
                data-e2e="input-funding-source-place-code"
                @update:model-value="setPlaceCode(row.override.placeOfServiceId, $event)"
              />
            </div>
          </hr-list-expansion>
          <hr-list-add-btn
            variant="flat"
            :label="i18n.t('settings.fundingSources.coding.actionAddPlaces')"
            :disable="addablePlaceOptions.length === 0"
            flush
            data-e2e="btn-funding-source-add-place"
            @click="placePicker?.show()"
          />
        </hr-list>
      </div>

      <!-- Payer-level, because the modifier describes the MODALITY rather than the service or the
           place: a payer that says 95 for telehealth means 95 on any telehealth claim. Clearing it
           is the no-modifier answer, which some payers want. -->
      <hr-list-select
        v-model="draft.telehealthModifier"
        class="col-12"
        :label="i18n.t('settings.fundingSources.coding.inputTelehealthModifierLabel')"
        :hint="i18n.t('settings.fundingSources.coding.inputTelehealthModifierHint')"
        :options="telehealthModifierOptions"
        clearable
        data-e2e="select-funding-source-telehealth-modifier"
      />

      <add-payer-rule-dialog
        ref="servicePicker"
        :title="i18n.t('settings.fundingSources.coding.actionAddServices')"
        :label="i18n.t('settings.fundingSources.coding.inputServicesLabel')"
        :options="addableServiceOptions"
        multiple
        @ok="addCoveredServices"
      />

      <add-payer-rule-dialog
        ref="placePicker"
        :title="i18n.t('settings.fundingSources.coding.actionAddPlaces')"
        :label="i18n.t('settings.fundingSources.coding.inputPlacesLabel')"
        :options="addablePlaceOptions"
        @ok="addPlaceOverrides"
      />

      <!-- Notes last: a free-text field that grows must never push a decision below the fold. -->
      <hr-text-field
        v-model="draft.notes"
        class="col-12"
        :label="i18n.t('settings.fundingSources.fields.notes')"
        multiline
        hide-bottom-space
        data-e2e="input-funding-source-notes"
      />
    </div>
  </hr-versioned-form-dialog>
</template>
